/**
 * Workflows
 *
 * 1) INTRINSIC WORKFLOW BEHAVIOR = fundamental to how that workflow works.
 *    Examples: Centralized allows adding pathology lab in QC Pending; Distributed LIMS has no
 *    create-order in portal; different status filters and order actions per workflow.
 *    These belong inside the workflow implementations.
 *
 * Target: per-workflow definitions (e.g. LdtWorkflowDefinition in @ldt/workflows) with
 * getStatusFilterOptions, getAvailableActions, etc.; capabilities stay minimal and cross-cutting.
 *
 * LDT workflow behavior (status filters, order actions) is implemented in @ldt/workflows:
 * one definition per workflow type, registered by workflowType. Call getLdtWorkflowDefinition(type)
 * and use .getStatusFilterOptions(userRoles), etc., instead of branching on workflowType.
 */
import {
  SAMD_EU_WORKFLOW,
  SAMD_DEMO_WORKFLOW,
  SAMD_UK_WORKFLOW,
  SAMD_US_WORKFLOW,
} from '@samd/workflows';
import { ancillaryLabWorkflow } from '@ldt/workflows/ancillary_lab';
import { clinicWorkflow } from '@ldt/workflows/clinic';
import { directUploadLabWorkflow } from '@ldt/workflows/direct_upload_lab';
import { tempusWorkflow } from '@ldt/workflows/tempus';
import { WorkflowDefinition } from 'src/distribution_platforms/features/workflow_definition';
import {
  OrganizationFieldsFragment,
  OrganizationWorkflowType,
} from 'src/types';
import { App } from 'src/types/app';
import { DistributionPlatform } from 'src/types/env';
import { WorkflowType } from 'src/types/workflows';

const ORG_WORKFLOW_TO_WORKFLOW: Record<OrganizationWorkflowType, WorkflowType> =
  {
    [OrganizationWorkflowType.Clinic]: WorkflowType.CLINIC,
    [OrganizationWorkflowType.AncillaryLab]: WorkflowType.ANCILLARY_LAB,
    [OrganizationWorkflowType.DirectUploadLab]: WorkflowType.DIRECT_UPLOAD_LAB,
    [OrganizationWorkflowType.Tempus]: WorkflowType.TEMPUS,
  };

/** API may send backend enum values ('clinic') or GraphQL values ('CLINIC'). Normalize and look up. */
function fromOrgWorkflowType(
  value: OrganizationWorkflowType | string,
): WorkflowType {
  const key = String(value).toUpperCase().replace(/-/g, '_');
  return (
    ORG_WORKFLOW_TO_WORKFLOW[key as OrganizationWorkflowType] ??
    WorkflowType.CLINIC
  );
}

const ANCILLARY_LAB_ORG_NAMES = [
  'pathnet',
  'ddx-foundation',
  'uro-partners',
  'avero-dx',
  'lumea-utah',
];

const SAMD_APP_TO_WORKFLOW: Record<
  App.SamdUs | App.SamdEu | App.SamdUk | App.SamdDemo,
  WorkflowType
> = {
  [App.SamdUs]: WorkflowType.SAMD_US,
  [App.SamdEu]: WorkflowType.SAMD_EU,
  [App.SamdUk]: WorkflowType.SAMD_UK,
  [App.SamdDemo]: WorkflowType.SAMD_DEMO,
};

function getSamdWorkflowFromApp(app: App): WorkflowType {
  if (app in SAMD_APP_TO_WORKFLOW) {
    return SAMD_APP_TO_WORKFLOW[app as keyof typeof SAMD_APP_TO_WORKFLOW];
  }

  throw new Error(`Unsupported app: ${app}`);
}

/**
 * Resolves workflow type for an organization and deployed app (VITE_APP).
 * SaMD: uses deployed app identity (VITE_APP).
 * LDT: uses org.workflowType from API when available; otherwise falls back to org-name list for ancillary labs.
 */
export function getWorkflowTypeForOrganization(
  org: OrganizationFieldsFragment,
  app: App,
): WorkflowType {
  if (app === App.Ldt) {
    const orgWorkflowType = org.workflowType;

    if (orgWorkflowType != null) {
      return fromOrgWorkflowType(orgWorkflowType);
    }

    // Fallback to ancillary lab org names if org workflow type is not available.
    // TODO(troy): Remove this fallback after we backfill the org workflow type in Auth0.
    return ANCILLARY_LAB_ORG_NAMES.includes(org.name!)
      ? WorkflowType.ANCILLARY_LAB
      : WorkflowType.CLINIC;
  }

  return getSamdWorkflowFromApp(app);
}

export function getWorkflowDefinition(
  platform: DistributionPlatform,
  workflowType: WorkflowType,
): WorkflowDefinition {
  if (platform === DistributionPlatform.Ldt) {
    if (workflowType === WorkflowType.CLINIC) {
      return clinicWorkflow;
    } else if (workflowType === WorkflowType.ANCILLARY_LAB) {
      return ancillaryLabWorkflow;
    } else if (workflowType === WorkflowType.DIRECT_UPLOAD_LAB) {
      return directUploadLabWorkflow;
    } else if (workflowType === WorkflowType.TEMPUS) {
      return tempusWorkflow;
    }

    throw new Error(`Unsupported LDT workflow type: ${workflowType}`);
  } else if (platform === DistributionPlatform.Samd) {
    if (workflowType === WorkflowType.SAMD_US) {
      return SAMD_US_WORKFLOW;
    } else if (workflowType === WorkflowType.SAMD_UK) {
      return SAMD_UK_WORKFLOW;
    } else if (workflowType === WorkflowType.SAMD_EU) {
      return SAMD_EU_WORKFLOW;
    } else if (workflowType === WorkflowType.SAMD_DEMO) {
      return SAMD_DEMO_WORKFLOW;
    }

    throw new Error(`Unsupported SaMD workflow type: ${workflowType}`);
  }

  throw new Error(`Unsupported platform: ${platform}`);
}
